import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Alert, Image, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';

const Volunteer: React.FC = () => {
  const [formData, setFormData] = useState({
    volunteerId: '',
    fullName: '',
    mobile: '',
    email: '',
    aadhar: '',
    gender: '',
    dateOfBirth: '',
    state: '',
    city: '',
    pinCode: '',
    address: '',
    skills: [] as string[],
    otherSkill: '',
    availability: '',
    volunteeringArea: '',
    previousExperience: 'No',
    experienceDetails: '',
    notRobot: false,
    photo: null as File | null,
  });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [previewPhoto, setPreviewPhoto] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false); // Modal visibility state

  useEffect(() => {
    const generateVolunteerId = () => {
      const randomId = 'VOL' + Math.floor(1000 + Math.random() * 9000);
      setFormData((prev) => ({ ...prev, volunteerId: randomId }));
    };
    generateVolunteerId();
  }, []);

  // Show modal and auto-close after 3 seconds
  useEffect(() => {
    if (successMessage || errorMessage) {
      setShowModal(true);
      const timer = setTimeout(() => {
        setShowModal(false);
        setSuccessMessage(null);
        setErrorMessage(null);
      }, 3000); // Modal closes after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMultiSelectChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    if (e.target instanceof HTMLSelectElement && e.target.multiple) {
      const options = e.target.selectedOptions;
      const values = Array.from(options, (option) => option.value);
      setFormData((prev) => ({ ...prev, skills: values }));
      if (!values.includes('Others')) setFormData((prev) => ({ ...prev, otherSkill: '' }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData((prev) => ({ ...prev, photo: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewPhoto(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, notRobot: e.target.checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.notRobot) {
      setErrorMessage('Please confirm that you are not a robot.');
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('volunteerId', formData.volunteerId);
    formDataToSend.append('fullName', formData.fullName);
    formDataToSend.append('mobile', formData.mobile);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('aadhar', formData.aadhar || '');
    formDataToSend.append('gender', formData.gender);
    formDataToSend.append('dateOfBirth', formData.dateOfBirth);
    formDataToSend.append('state', formData.state);
    formDataToSend.append('city', formData.city);
    formDataToSend.append('pinCode', formData.pinCode);
    formDataToSend.append('address', formData.address);
    formDataToSend.append('skills', JSON.stringify(formData.skills));
    formDataToSend.append('otherSkill', formData.otherSkill);
    formDataToSend.append('availability', formData.availability);
    formDataToSend.append('volunteeringArea', formData.volunteeringArea);
    formDataToSend.append('previousExperience', formData.previousExperience);
    formDataToSend.append('experienceDetails', formData.experienceDetails || '');
    if (formData.photo) {
      formDataToSend.append('photo', formData.photo);
    }

    try {
      const res = await fetch(process.env.REACT_APP_API_URL + '/api/volunteers', {
        method: 'POST',
        body: formDataToSend,
      });

      const data = await res.json();
      if (res.ok) {
        setSuccessMessage(`Thank you for registering as a volunteer!`);
        const randomId = 'VOL' + Math.floor(1000 + Math.random() * 9000);
        setFormData({
          volunteerId: randomId,
          fullName: '',
          mobile: '',
          email: '',
          aadhar: '',
          gender: '',
          dateOfBirth: '',
          state: '',
          city: '',
          pinCode: '',
          address: '',
          skills: [],
          otherSkill: '',
          availability: '',
          volunteeringArea: '',
          previousExperience: 'No',
          experienceDetails: '',
          notRobot: false,
          photo: null,
        });
        setPreviewPhoto(null);
        setErrorMessage(null);
      } else {
        setErrorMessage(data.message || 'Error submitting form.');
      }
    } catch (err) {
      setErrorMessage('Error submitting form. Please try again.');
    }
  };

  return (
    <>
      {/* Hero Section */}
      <section
        className="hero-section text-white text-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://i.pinimg.com/736x/e3/f5/ec/e3f5eccd40b6850087d9e29b36c0d18d.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '30px',
        }}
      >
        <div>
          <h1 className="display-4 mb-3">Join Us as a Volunteer</h1>
          <h4 className="mb-3">Make a Difference Today</h4>
          <p className="lead">
            Be a part of our mission to empower communities through education, health, and sustainable development.
          </p>
        </div>
      </section>

      {/* Volunteer Form Section */}
      <section className="volunteer-section py-5">
        <Container>
          {/*  <h2 className="text-center mb-5 volunteer-title">Become a Volunteer</h2>*/}
          <div className="volunteer-form-box p-4">
            {previewPhoto && (
              <div className="photo-preview mb-3 text-center">
                <Image src={previewPhoto} alt="Preview" rounded fluid style={{ maxWidth: '200px' }} />
              </div>
            )}
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3" controlId="formVolunteerId">
                <Form.Label className="d-none">Volunteer ID</Form.Label>
                <Form.Control
                  type="text"
                  name="volunteerId"
                  value={formData.volunteerId}
                  readOnly
                  className="volunteer-id-field d-none"
                />
              </Form.Group>
              {/* Row 1: Photo and Full Name */}
              <div className="row mb-3">
               
                <div className="col-md-12">
                  <Form.Group controlId="formFullName">
                    <Form.Label>Full Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      required
                    />
                  </Form.Group>
                </div>
              </div>
              {/* Row 2: Mobile and Email */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Group controlId="formMobile">
                    <Form.Label>Mobile</Form.Label>
                    <Form.Control
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      placeholder="Enter your mobile number"
                      pattern="[0-9]{10}"
                      required
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group controlId="formEmail">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      required
                    />
                  </Form.Group>
                </div>
              </div>
              {/* Row 3: Aadhar and Gender */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Group controlId="formAadhar">
                    <Form.Label>Aadhar (Optional)</Form.Label>
                    <Form.Control
                      type="text"
                      name="aadhar"
                      value={formData.aadhar}
                      onChange={handleChange}
                      placeholder="Enter your Aadhar number"
                      pattern="[0-9]{12}"
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group controlId="formGender">
                    <Form.Label>Gender</Form.Label>
                    <Form.Control as="select" name="gender" value={formData.gender} onChange={handleChange} required>
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </Form.Control>
                  </Form.Group>
                </div>
              </div>
              {/* Row 4: Date of Birth and State */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Group controlId="formDateOfBirth">
                    <Form.Label>Date of Birth</Form.Label>
                    <Form.Control
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group controlId="formState">
                    <Form.Label>State</Form.Label>
                    <Form.Control
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      placeholder="Enter your state"
                      required
                    />
                  </Form.Group>
                </div>
              </div>
              {/* Row 5: City and Pin Code */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Group controlId="formCity">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Enter your city"
                      required
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group controlId="formPinCode">
                    <Form.Label>Pin Code</Form.Label>
                    <Form.Control
                      type="text"
                      name="pinCode"
                      value={formData.pinCode}
                      onChange={handleChange}
                      placeholder="Enter your pin code"
                      pattern="[0-9]{6}"
                      required
                    />
                  </Form.Group>
                </div>
              </div>
              {/* Row 6: Address (Full Width) */}
              <div className="row mb-3">
                <div className="col-md-12">
                  <Form.Group controlId="formAddress">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Enter your address"
                      required
                    />
                  </Form.Group>
                </div>
              </div>
              {/* Row 7: Skills (Full Width) */}
              <div className="row mb-3">
                <div className="col-md-12">
                  <Form.Group controlId="formSkills">
                    <Form.Label>Skills & Expertise</Form.Label>
                    <Form.Control
                      as="select"
                      name="skills"
                      value={formData.skills}
                      onChange={handleMultiSelectChange}
                      multiple
                      required
                    >
                      <option value="Technical">Technical</option>
                      <option value="Teaching">Teaching</option>
                      <option value="IT">IT</option>
                      <option value="Others">Others</option>
                    </Form.Control>
                    {formData.skills.includes('Others') && (
                      <Form.Control
                        type="text"
                        name="otherSkill"
                        value={formData.otherSkill}
                        onChange={handleChange}
                        placeholder="Specify other skill"
                        className="mt-2"
                      />
                    )}
                  </Form.Group>
                </div>
              </div>
              {/* Row 8: Availability and Volunteering Area */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Group controlId="formAvailability">
                    <Form.Label>Availability</Form.Label>
                    <Form.Control
                      as="select"
                      name="availability"
                      value={formData.availability}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Availability</option>
                      <option value="DAY">Day</option>
                      <option value="Hours">Hours</option>
                      <option value="Weekend">Weekend</option>
                    </Form.Control>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group controlId="formVolunteeringArea">
                    <Form.Label>Preferred Volunteering Area</Form.Label>
                    <Form.Control
                      as="select"
                      name="volunteeringArea"
                      value={formData.volunteeringArea}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Area</option>
                      <option value="Education">Education</option>
                      <option value="Women Empowerment">Women Empowerment</option>
                      <option value="Environment">Environment</option>
                      <option value="Health">Health</option>
                      <option value="Livelihood">Livelihood</option>
                      <option value="Fundraising">Fundraising</option>
                      <option value="Administrator">Administrator</option>
                    </Form.Control>
                  </Form.Group>
                </div>
              </div>
              {/* Row 9: Previous Experience (Full Width) */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Group controlId="formPreviousExperience">
                    <Form.Label>Previous Volunteer Experience</Form.Label>
                    <Form.Control
                      as="select"
                      name="previousExperience"
                      value={formData.previousExperience}
                      onChange={handleChange}
                      required
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </Form.Control>
                    {formData.previousExperience === 'Yes' && (
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="experienceDetails"
                        value={formData.experienceDetails}
                        onChange={handleChange}
                        placeholder="Provide details"
                        className="mt-2"
                      />
                    )}
                  </Form.Group>
                </div>
                 <div className="col-md-6">
                  <Form.Group controlId="formPhoto">
                    <Form.Label>Upload Photo (Optional)</Form.Label>
                    <Form.Control type="file" name="photo" accept="image/*" onChange={handleFileChange} />
                  </Form.Group>
                </div>
              </div>
              {/* Row 10: Not Robot (Full Width) */}
              <div className="row mb-3">
                <div className="col-md-12">
                  <Form.Group controlId="formNotRobot">
                    <Form.Check
                      type="checkbox"
                      label="I accept the Terms and Conditions"
                      name="notRobot"
                      checked={formData.notRobot}
                      onChange={handleCheckboxChange}
                      required
                    />
                  </Form.Group>
                </div>
              </div>
              <div className="text-center mt-4">
                <Button variant="primary" type="submit" className="volunteer-submit-btn">
                  Submit
                </Button>
              </div>
            </Form>
          </div>
        </Container>
      </section>

      {/* Success/Error Modal */}
      <Modal show={showModal} centered>
        <Modal.Body className="text-center">
          {successMessage ? (
            <div className="d-flex flex-column align-items-center">
              <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-success mb-3" />
              <h4 className="text-success mb-2">Success!</h4>
              <p>{successMessage}</p>
            </div>
          ) : (
            <div className="d-flex flex-column align-items-center">
              <FontAwesomeIcon icon={faTimesCircle} size="3x" className="text-danger mb-3" />
              <h4 className="text-danger mb-2">Error!</h4>
              <p>{errorMessage}</p>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Volunteer;